import { Controller } from '@hotwired/stimulus';
import { MQ } from '../tools/MQ';

export default class extends Controller {
	static targets = ['fragment', 'link'];
	static values = {
		orientation: { type: String, default: 'horizontal' },
	};

	connect() {
		this.initializeTabs();
	}

	initializeTabs() {
		const selectedTab = this.linkTargets.find((link) => link.getAttribute('aria-selected') === 'true') || this.linkTargets[0];

		this.linkTargets.forEach((link, index) => {
			const panelId = link.getAttribute('aria-controls') || `tabpanel-${index + 1}`;
			const fragment = this.fragmentTargets.find((e) => e.id === panelId) || document.getElementById(panelId);

			link.setAttribute('role', 'tab');
			link.setAttribute('aria-selected', 'false');
			link.setAttribute('aria-controls', panelId);
			link.setAttribute('tabindex', '-1');

			link.addEventListener('keydown', (e) => this.onKeydown(e));
			link.addEventListener('click', (e) => this.switch(e));
			link.addEventListener('focus', () => this.scrollTabIntoView(link));

			if (fragment) {
				fragment.setAttribute('role', 'tabpanel');
				fragment.setAttribute('aria-labelledby', link.id || `tab-${index + 1}`);
				if (index > 0) {
					fragment.setAttribute('hidden', '');
				}
			}
		});

		if (selectedTab) {
			this.setSelectedTab(selectedTab, false);
		}
	}

	setSelectedTab(currentTab, setFocus = true) {
		const panelId = currentTab.getAttribute('aria-controls') || `tabpanel-${Array.from(this.linkTargets).indexOf(currentTab) + 1}`;
		const fragment = this.fragmentTargets.find((e) => e.id === panelId) || document.getElementById(panelId);

		this.linkTargets.forEach((link) => {
			link.classList.remove('is-active');
			link.setAttribute('aria-selected', 'false');
			link.setAttribute('tabindex', '-1');
		});

		this.fragmentTargets.forEach((item) => {
			item.classList.remove('is-active');
			item.setAttribute('hidden', '');
		});

		currentTab.classList.add('is-active');
		currentTab.setAttribute('aria-selected', 'true');
		currentTab.removeAttribute('tabindex');

		if (fragment) {
			fragment.classList.add('is-active');
			fragment.removeAttribute('hidden');
		}

		if (setFocus) {
			currentTab.focus();
			this.scrollTabIntoView(currentTab);
		}
	}

	scrollTabIntoView(tabEl) {
		if (!tabEl?.scrollIntoView) return;
		tabEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
	}

	setSelectedToPreviousTab(currentTab) {
		const currentIndex = this.linkTargets.indexOf(currentTab);
		const previousIndex = currentIndex === 0 ? this.linkTargets.length - 1 : currentIndex - 1;
		this.setSelectedTab(this.linkTargets[previousIndex]);
	}

	setSelectedToNextTab(currentTab) {
		const currentIndex = this.linkTargets.indexOf(currentTab);
		const nextIndex = currentIndex === this.linkTargets.length - 1 ? 0 : currentIndex + 1;
		this.setSelectedTab(this.linkTargets[nextIndex]);
	}

	switch = (event) => {
		event.preventDefault();
		this.setSelectedTab(event.currentTarget);
	};

	onKeydown(event) {
		const currentTab = event.currentTarget;
		let flag = false;

		const mdUp = MQ('mdUp');
		const isVertical = this.orientationValue === 'vertical' && mdUp;

		switch (event.key) {
			case 'ArrowLeft':
				if (isVertical) break;
				this.setSelectedToPreviousTab(currentTab);
				flag = true;
				break;

			case 'ArrowRight':
				if (isVertical) break;
				this.setSelectedToNextTab(currentTab);
				flag = true;
				break;

			case 'ArrowUp':
				if (!isVertical) break;
				this.setSelectedToPreviousTab(currentTab);
				flag = true;
				break;

			case 'ArrowDown':
				if (!isVertical) break;
				this.setSelectedToNextTab(currentTab);
				flag = true;
				break;

			case 'Home':
				this.setSelectedTab(this.linkTargets[0]);
				flag = true;
				break;

			case 'End':
				this.setSelectedTab(this.linkTargets[this.linkTargets.length - 1]);
				flag = true;
				break;

			default:
				break;
		}

		if (flag) {
			event.stopPropagation();
			event.preventDefault();
		}
	}

	close() {
		this.linkTargets.forEach((link) => {
			link.classList.remove('is-active');
			link.setAttribute('aria-selected', 'false');
			link.setAttribute('tabindex', '-1');
		});
		this.fragmentTargets.forEach((item) => {
			item.classList.remove('is-active');
			item.setAttribute('hidden', '');
		});
	}
}
